import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Download,
  Upload,
  FileText,
  Plus,
  Calendar,
  Edit,
  Trash2,
  Eye,
  User,
  Building,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  FileSpreadsheet,
  Timer,
  Activity,
  TrendingUp,
  UserCheck,
  Signature,
  FileCheck
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { contractService } from "../services/contractService";




// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};

export default function ContratsPage() {
  // États pour la gestion des contrats
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTab, setCurrentTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signatureCode, setSignatureCode] = useState("");
  const [contractToSign, setContractToSign] = useState(null);
  const [agencySignature, setAgencySignature] = useState(null);
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isSigningContract, setIsSigningContract] = useState(false);

  const itemsPerPage = 12;

  // Les contrats seront chargés depuis le backend

  // Chargement des contrats depuis le backend
  useEffect(() => {
    const loadContracts = async () => {
      setIsLoading(true);
      try {
        const data = await contractService.getClientContracts();
        console.log('Contrats reçus du backend:', data);
        setContracts(data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des contrats:', error);
        toast.error('Erreur lors du chargement des contrats');
        setContracts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadContracts();
  }, []);


  // Fonctions utilitaires
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      // Si la date est au format YYYYMMDD (ex: 20251010)
      if (typeof dateString === 'string' && dateString.length === 8 && /^\d{8}$/.test(dateString)) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        const date = new Date(year, month - 1, day); // month - 1 car les mois commencent à 0
        if (isNaN(date.getTime())) return "-";
        return format(date, "dd/MM/yyyy", { locale: fr });
      }
      // Sinon, traiter comme une date normale
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      return format(date, "dd/MM/yyyy", { locale: fr });
    } catch (error) {
      return "-";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      return format(date, "dd/MM/yyyy 'à' HH:mm", { locale: fr });
    } catch (error) {
      return "-";
    }
  };

  // Fonction pour obtenir le statut d'un contrat
  const getContractStatus = (contract) => {
    if (!contract.signedByUser && !contract.signedByClient) {
      return "non_signe";
    } else if (contract.signedByUser && !contract.signedByClient) {
      return "attente_client";
    } else if (!contract.signedByUser && contract.signedByClient) {
      return "attente_interimaire";
    } else if (contract.signedByUser && contract.signedByClient) {
      return "signe_complet";
    }
    return "inconnu";
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch(status) {
      case "non_signe":
        return "Non signé";
      case "attente_client":
        return "En attente client";
      case "attente_interimaire":
        return "En attente intérimaire";
      case "signe_complet":
        return "Signé complet";
      case "en_cours":
        return "En cours";
      case "termine":
        return "Terminé";
      default:
        return status;
    }
  };

  // Calcul des totaux pour l'affichage des statistiques
  const calculerTotaux = () => {
    if (!contracts.length) {
      return {
        totalContrats: 0,
        contratsNonSignes: 0,
        contratsAttenteClient: 0,
        contratsSignesComplet: 0,
        contratsEnCours: 0
      };
    }

    const contratsNonSignes = contracts.filter(c => getContractStatus(c) === "non_signe").length;
    const contratsAttenteClient = contracts.filter(c => getContractStatus(c) === "attente_client").length;
    const contratsSignesComplet = contracts.filter(c => getContractStatus(c) === "signe_complet").length;
    const contratsEnCours = contracts.filter(c => c.StatutValidation === "en_cours").length;

    return {
      totalContrats: contracts.length,
      contratsNonSignes,
      contratsAttenteClient,
      contratsSignesComplet,
      contratsEnCours
    };
  };

  const totaux = calculerTotaux();


  // Fonction de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Calcul des contrats filtrés et triés
  const filteredContracts = contracts
    .filter((contract) => {
      const status = getContractStatus(contract);

      // Filtre par onglet
      if (currentTab === "non_signe" && status !== "non_signe") return false;
      if (currentTab === "attente_client" && status !== "attente_client") return false;
      if (currentTab === "signe_complet" && status !== "signe_complet") return false;
      if (currentTab === "en_cours" && contract.StatutValidation !== "en_cours") return false;

      // Si champ vide, on garde tout
      if (searchTerm === "") return true;

      const search = searchTerm.toLowerCase();

      // Recherche dans tous les champs pertinents
      return (
        contract.NomPrenom?.toLowerCase().includes(search) ||
        contract.TxtQualification?.toLowerCase().includes(search) ||
        contract.RaisonSociale?.toLowerCase().includes(search) ||
        contract.IdContrat?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      let valueA, valueB;

      switch (sortField) {
        case "NomPrenom":
          valueA = a.NomPrenom;
          valueB = b.NomPrenom;
          break;
        case "status":
          valueA = getContractStatus(a);
          valueB = getContractStatus(b);
          break;
        case "TauxHoraireFacture":
          valueA = parseFloat(a.TauxHoraireFacture) || 0;
          valueB = parseFloat(b.TauxHoraireFacture) || 0;
          break;
        case "DateDebut":
          valueA = a.DateDebut;
          valueB = b.DateDebut;
          break;
        case "RaisonSociale":
          valueA = a.RaisonSociale;
          valueB = b.RaisonSociale;
          break;
        default:
          valueA = a[sortField];
          valueB = b[sortField];
      }

      // Comparaison de dates
      if (sortField === "DateDebut") {
        const dateA = valueA ? new Date(valueA) : null;
        const dateB = valueB ? new Date(valueB) : null;

        valueA = dateA && !isNaN(dateA.getTime()) ? dateA.getTime() : 0;
        valueB = dateB && !isNaN(dateB.getTime()) ? dateB.getTime() : 0;
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  // S'assurer que currentPage est dans les limites valides
  const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));

  // Si la page actuelle n'est pas valide, la corriger
  if (validCurrentPage !== currentPage && totalPages > 0) {
    setCurrentPage(validCurrentPage);
  }

  const startIndex = (validCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, endIndex);


  // Fonctions pour les actions
  const handleViewContract = async (contractId) => {
    try {
      const contract = contracts.find(c => c._id === contractId);
      if (!contract) {
        toast.error("Contrat non trouvé");
        return;
      }

      // Vérifier si le contrat a été signé (et donc a un PDF disponible)
      if (!contract.signedByClient && !contract.signedByUser) {
        toast.warning("Le contrat doit être signé avant de pouvoir être visualisé");
        return;
      }

      // Ouvrir le contrat dans un nouvel onglet
      const url = await contractService.getContractViewUrl(contractId);
      window.open(url, '_blank');
    } catch (error) {
      console.error("Erreur lors de l'ouverture du contrat:", error);
      if (error.response?.status === 404) {
        toast.error("PDF du contrat non disponible");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé à ce contrat");
      } else {
        toast.error("Erreur lors de l'ouverture du contrat");
      }
    }
  };

  const handleDownloadContract = async (contractId,contractNumber) => {
    try {
      const contract = contracts.find(c => c._id === contractId);
      if (!contract) {
        toast.error("Contrat non trouvé");
        return;
      }

      // Vérifier si le contrat a été signé (et donc a un PDF disponible)
      if (!contract.signedByClient && !contract.signedByUser) {
        toast.warning("Le contrat doit être signé avant de pouvoir être téléchargé");
        return;
      }

      await contractService.downloadContract(contractId,contractNumber);
      toast.success("Contrat téléchargé avec succès");
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      if (error.response?.status === 404) {
        toast.error("PDF du contrat non disponible");
      } else if (error.response?.status === 403) {
        toast.error("Accès non autorisé à ce contrat");
      } else {
        toast.error("Erreur lors du téléchargement du contrat");
      }
    }
  };

  const handleSignContract = async (contract) => {
    setIsGeneratingCode(true);
    try {
      // Générer le code de signature
      await contractService.generateSignatureCode([contract._id]);
      setContractToSign(contract);

      // Charger la signature de l'agence à la demande
      if (!agencySignature) {
        setLoadingSignature(true);
        try {
          const signature = await contractService.getAgencySignature();
          setAgencySignature(signature);
        } catch (error) {
          console.error("Erreur lors du chargement de la signature:", error);
        } finally {
          setLoadingSignature(false);
        }
      }

      setIsSignatureDialogOpen(true);
      toast.success("Code de signature envoyé par email");
    } catch (error) {
      console.error("Erreur lors de la génération du code:", error);
      toast.error("Erreur lors de la génération du code de signature");
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleSignatureSubmit = async () => {
    if (!signatureCode || signatureCode.length !== 6) {
      toast.error("Veuillez saisir un code de signature valide (6 chiffres)");
      return;
    }

    setIsSigningContract(true);
    try {
      // Signer le contrat avec le code
      const contractIds = selectedContracts.length > 0 ? selectedContracts : [contractToSign._id];
      await contractService.signContract(contractIds, signatureCode);

      // Mettre à jour les contrats localement
      setContracts(prev => prev.map(c =>
        contractIds.includes(c._id)
          ? { ...c, signedByClient: true, clientSignedDate: new Date().toISOString() }
          : c
      ));

      toast.success(`${contractIds.length > 1 ? contractIds.length + ' contrats signés' : 'Contrat signé'} avec succès !`);
      setIsSignatureDialogOpen(false);
      setSignatureCode("");
      setContractToSign(null);
      setSelectedContracts([]);
    } catch (error) {
      console.error("Erreur lors de la signature:", error);
      toast.error("Code de signature invalide ou erreur lors de la signature");
    } finally {
      setIsSigningContract(false);
    }
  };

  const handleExportExcel = () => {
    toast.success("Export Excel en cours de développement");
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const data = await contractService.getClientContracts();
      console.log('Contrats actualisés:', data);
      setContracts(data || []);
      toast.success("Contrats actualisés");
    } catch (error) {
      console.error('Erreur lors de l\'actualisation:', error);
      toast.error('Erreur lors de l\'actualisation des contrats');
      setContracts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonctions pour la sélection multiple
  const handleSelectContract = (contractId) => {
    setSelectedContracts(prev =>
      prev.includes(contractId)
        ? prev.filter(id => id !== contractId)
        : [...prev, contractId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContracts.length === paginatedContracts.length) {
      setSelectedContracts([]);
    } else {
      setSelectedContracts(paginatedContracts.map(c => c._id));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedContracts.length === 0) {
      toast.error("Veuillez sélectionner au moins un contrat");
      return;
    }

    try {
      for (const contractId of selectedContracts) {
        await contractService.downloadContract(contractId);
      }
      toast.success(`${selectedContracts.length} contrat(s) téléchargé(s)`);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      toast.error("Erreur lors du téléchargement des contrats");
    }
  };

  const handleBulkSign = async () => {
    if (selectedContracts.length === 0) {
      toast.error("Veuillez sélectionner au moins un contrat");
      return;
    }

    setIsGeneratingCode(true);
    try {
      // Générer le code de signature pour tous les contrats sélectionnés
      await contractService.generateSignatureCode(selectedContracts);

      // Charger la signature de l'agence à la demande
      if (!agencySignature) {
        setLoadingSignature(true);
        try {
          const signature = await contractService.getAgencySignature();
          setAgencySignature(signature);
        } catch (error) {
          console.error("Erreur lors du chargement de la signature:", error);
        } finally {
          setLoadingSignature(false);
        }
      }

      setIsSignatureDialogOpen(true);
      toast.success("Code de signature envoyé par email");
    } catch (error) {
      console.error("Erreur lors de la génération du code:", error);
      toast.error("Erreur lors de la génération du code de signature");
    } finally {
      setIsGeneratingCode(false);
    }
  };



  return (
    <div className="min-h-screen">
      <motion.div
        className="container mx-auto py-8 px-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* En-tête moderne */}
        <motion.div
          className="mb-8"
          variants={itemVariants}
        >
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <FileText size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Contrats de Mise à Disposition
              </h1>
              <p className="text-slate-600 mt-1">
                Gérez et suivez tous vos contrats en temps réel
              </p>
            </div>
          </div>
        </motion.div>

        {/* Statistiques modernes */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          variants={itemVariants}
        >
          {/* Total Contrats */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Contrats</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{totaux.totalContrats}</p>
                <p className="text-blue-600 text-sm mt-1 flex items-center">
                  <Activity size={16} className="mr-1" />
                  Tous statuts confondus
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FileText size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          {/* Non Signés */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Non Signés</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{totaux.contratsNonSignes}</p>
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  Action requise
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertCircle size={24} className="text-red-600" />
              </div>
            </div>
          </div>

          {/* En Attente Client */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Attente Client</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">{totaux.contratsAttenteClient}</p>
                <p className="text-orange-600 text-sm mt-1 flex items-center">
                  <Clock size={16} className="mr-1" />
                  En attente signature
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl">
                <Clock size={24} className="text-orange-600" />
              </div>
            </div>
          </div>

          {/* Signés Complet */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Signés Complet</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{totaux.contratsSignesComplet}</p>
                <p className="text-green-600 text-sm mt-1 flex items-center">
                  <CheckCircle size={16} className="mr-1" />
                  Finalisés
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle size={24} className="text-green-600" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filtres et recherche modernes */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
          variants={itemVariants}
        >
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            {/* Onglets de filtrage */}
            <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => {
                  setCurrentTab("all");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "all"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText size={18} />
                  <span>Tous ({totaux.totalContrats})</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setCurrentTab("non_signe");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "non_signe"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <AlertCircle size={18} />
                  <span>Non signés ({totaux.contratsNonSignes})</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setCurrentTab("attente_client");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "attente_client"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Clock size={18} />
                  <span>Attente client ({totaux.contratsAttenteClient})</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setCurrentTab("signe_complet");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "signe_complet"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CheckCircle size={18} />
                  <span>Signés ({totaux.contratsSignesComplet})</span>
                </div>
              </button>
            </div>

           
          </div>
           {/* Barre de recherche et actions */}
            <div className="flex items-center mt-4 space-x-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center space-x-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedContracts.length === paginatedContracts.length && paginatedContracts.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span>Tout sélectionner</span>
              </button>
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, poste, client..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-3 w-80 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setSearchTerm("")}
                    title="Effacer"
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>

              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
                <span>Actualiser</span>
              </button>

              
            </div>
        </motion.div>

        {/* Barre d'actions pour la sélection multiple */}
        {selectedContracts.length > 0 && (
          <motion.div
            className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle size={20} className="text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {selectedContracts.length} contrat(s) sélectionné(s)
                  </span>
                </div>
                <button
                  onClick={() => setSelectedContracts([])}
                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Tout désélectionner
                </button>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleBulkDownload}
                  className="flex items-center space-x-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  <Download size={18} />
                  <span>Télécharger</span>
                </button>

                <button
                  onClick={handleBulkSign}
                  disabled={isGeneratingCode}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isGeneratingCode ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Signature size={18} />
                  )}
                  <span>Signer</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Liste des contrats en cards modernes */}
        <motion.div
          className="space-y-4"
          variants={itemVariants}
        >
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <FileText size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">Aucun contrat trouvé</h3>
              <p className="text-slate-500">
                {searchTerm ? "Aucun contrat ne correspond à votre recherche." : "Aucun contrat disponible pour le moment."}
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {paginatedContracts.map((contract, index) => (
                <motion.div
                  key={contract._id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-300"
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  layout
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center gap-4">
                    {/* Checkbox de sélection */}
                    <div className="flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedContracts.includes(contract._id)}
                        onChange={() => handleSelectContract(contract._id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                    </div>

                    {/* Contenu principal en grille */}
                    <div className="flex-1 grid grid-cols-1 xl:grid-cols-6 lg:grid-cols-3 md:grid-cols-2 gap-4 items-center">

                    {/* Intérimaire */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <User size={20} className="text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 truncate">{contract.NomPrenom}</p>
                        <p className="text-sm text-slate-500">{contract.TxtQualification}</p>
                        <p className="text-xs text-slate-400">ID: {contract.IdContrat}</p>
                      </div>
                    </div>

                    {/* Période */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                        <Calendar size={20} className="text-indigo-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800">{formatDate(contract.DateDebut)}</p>
                        <p className="text-sm text-slate-500">au {formatDate(contract.DateFin)}</p>
                      </div>
                    </div>

                    {/* Tarif */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                        <Timer size={20} className="text-emerald-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800">{contract.TauxHoraireFacture}€/h</p>
                        <p className="text-sm text-slate-500">{contract.NbHeureHebdo}h/semaine</p>
                      </div>
                    </div>

                    {/* Statut */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                        getContractStatus(contract) === "signe_complet"
                          ? "bg-emerald-100"
                          : getContractStatus(contract) === "attente_client"
                          ? "bg-orange-100"
                          : getContractStatus(contract) === "attente_interimaire"
                          ? "bg-yellow-100"
                          : "bg-red-100"
                      }`}>
                        {getContractStatus(contract) === "signe_complet" ? (
                          <CheckCircle size={20} className="text-emerald-600" />
                        ) : getContractStatus(contract) === "attente_client" ? (
                          <Clock size={20} className="text-orange-600" />
                        ) : getContractStatus(contract) === "attente_interimaire" ? (
                          <Timer size={20} className="text-yellow-600" />
                        ) : (
                          <AlertCircle size={20} className="text-red-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          getContractStatus(contract) === "signe_complet"
                            ? "bg-emerald-100 text-emerald-800"
                            : getContractStatus(contract) === "attente_client"
                            ? "bg-orange-100 text-orange-800"
                            : getContractStatus(contract) === "attente_interimaire"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {getStatusLabel(getContractStatus(contract))}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Créé: {formatDate(contract.createdAt)}
                        </p>
                        
                      </div>
                    </div>

                    {/* Signatures */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                        <Signature size={20} className="text-slate-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-1">
                          {contract.signedByUser ? (
                            <CheckCircle size={14} className="text-green-600" />
                          ) : (
                            <XCircle size={14} className="text-red-600" />
                          )}
                          <span className="text-xs text-slate-600">Intérimaire</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {contract.signedByClient ? (
                            <CheckCircle size={14} className="text-green-600" />
                          ) : (
                            <XCircle size={14} className="text-red-600" />
                          )}
                          <span className="text-xs text-slate-600">
                            Client
                            
                          </span>
                        </div>
                      </div>
                    </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-2 flex-shrink-0">
                      <button
                        onClick={() => handleViewContract(contract._id)}
                        disabled={!contract.signedByClient && !contract.signedByUser}
                        className={`p-2 rounded-lg transition-colors ${
                          contract.signedByClient || contract.signedByUser
                            ? "text-blue-600 hover:bg-blue-100"
                            : "text-slate-400 cursor-not-allowed"
                        }`}
                        title={
                          contract.signedByClient || contract.signedByUser
                            ? "Voir le contrat"
                            : "Contrat non signé - PDF non disponible"
                        }
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleDownloadContract(contract._id,contract.NumeroContrat+"_"+contract.NumeroAvenant)}
                        disabled={!contract.signedByClient && !contract.signedByUser}
                        className={`p-2 rounded-lg transition-colors ${
                          contract.signedByClient || contract.signedByUser
                            ? "text-green-600 hover:bg-green-100"
                            : "text-slate-400 cursor-not-allowed"
                        }`}
                        title={
                          contract.signedByClient || contract.signedByUser
                            ? "Télécharger le contrat"
                            : "Contrat non signé - PDF non disponible"
                        }
                      >
                        <Download size={18} />
                      </button>
                      {!contract.signedByClient && (
                        <button
                          onClick={() => handleSignContract(contract)}
                          className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                          title="Signer le contrat"
                        >
                          <Signature size={18} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Informations de signature */}
                  {(contract.signedByUser || contract.signedByClient) && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm">
                        <FileCheck size={16} className="text-green-600 flex-shrink-0" />
                        <div className="flex-1">
                          {contract.signedByUser && (
                            <p className="text-green-800">
                              <span className="font-medium">Intérimaire:</span> Signé le {formatDateTime(contract.userSignedDate)}
                            </p>
                          )}
                          {contract.signedByClient && (
                            <div className="text-green-800">
                              <p>
                                <span className="font-medium">Client:</span> Signé le {formatDateTime(contract.clientSignedDate)}
                                {contract.clientSignedBy && (
                                <span className="text-sm ml-2 text-green-700 mt-1">
                                  Par: {contract.clientSignedBy.firstname} {contract.clientSignedBy.lastname} ({contract.clientSignedBy.email})
                                </span>
                              )}
                              </p>
                              
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </motion.div>

        {/* Pagination moderne */}
        {totalPages > 1 && (
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-8"
            variants={itemVariants}
          >
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-slate-600">
                Affichage de {startIndex + 1} à {Math.min(endIndex, filteredContracts.length)} sur {filteredContracts.length} contrats
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, validCurrentPage - 1))}
                  disabled={validCurrentPage === 1}
                  className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {totalPages > 0 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, validCurrentPage - 2)) + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        validCurrentPage === pageNum
                          ? "bg-blue-500 text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, validCurrentPage + 1))}
                  disabled={validCurrentPage === totalPages || totalPages === 0}
                  className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Modal de signature */}
        {isSignatureDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Signature size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Signature électronique</h3>
                  <p className="text-sm text-slate-600">Contrat {contractToSign?.IdContrat}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-slate-700 mb-4">
                  {selectedContracts.length > 0
                    ? `Pour signer ${selectedContracts.length} contrat(s), veuillez saisir le code de signature à 6 chiffres qui vous a été envoyé par email.`
                    : "Pour signer ce contrat, veuillez saisir le code de signature à 6 chiffres qui vous a été envoyé par email."
                  }
                </p>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Code de signature
                  </label>
                  <input
                    type="text"
                    value={signatureCode}
                    onChange={(e) => setSignatureCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-widest"
                    maxLength={6}
                  />
                  <p className="text-xs text-slate-500">
                    Saisissez les 6 chiffres reçus par email
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setIsSignatureDialogOpen(false);
                    setSignatureCode("");
                    setContractToSign(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSignatureSubmit}
                  disabled={signatureCode.length !== 6 || isSigningContract}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {isSigningContract ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Signature en cours...</span>
                    </>
                  ) : (
                    <span>{selectedContracts.length > 0 ? `Signer ${selectedContracts.length} contrat(s)` : 'Signer le contrat'}</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
